import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ClipboardList, MapPin, LineChart, CheckCircle2 } from 'lucide-react';
import { useReveal } from '../hooks/useInView';
import { PORTAL_DASHBOARD_URL } from '../utils/ctaUrl';

const STEPS = [
  {
    n: 1,
    icon: ClipboardList,
    eyebrow: 'Eligibility quiz',
    title: 'Start with a quick assessment..',
    text: 'Answer a few questions about your BMI, health history and goals so we can guide you toward a suitable doctor consultation and programme pathway.',
    href: '/intake',
    cta: 'Start assessment',
  },
  {
    n: 2,
    icon: MapPin,
    eyebrow: 'PARTNER CLINICS',
    title: 'Find a suitable clinic for you.',
    text: 'Clinic options are presented based on location, consultation needs, and programme preferences',
    // href: '/why-us',
    // cta: '',
  },
  {
    n: 3,
    icon: LineChart,
    eyebrow: 'PROGRESS MONITORING',
    title: 'Track your progress over time.',
    text: 'Keep your check-ins, progress updates, and follow-up notes in one private dashboard. ',
    href: PORTAL_DASHBOARD_URL,
    external: true,
    cta: 'View Dashboard',
  },
];

function StepCard({ t, i }) {
  const r = useReveal({ threshold: 0.1 });
  const Icon = t.icon;
  const isClickable = Boolean(t.href);
  const hasCta = Boolean(t.cta);
  const cardBase =
    'group relative flex flex-col h-full card-soft p-7 transition-all duration-300';
  const cardClickable = 'hover:shadow-card hover:-translate-y-1 cursor-pointer';
  const inner = (
    <>
      {/* Big numbered chip in the top-right */}
      <span className="absolute top-5 right-5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-brand-700 text-white text-sm font-semibold tabular-nums shadow-card">
        {t.n}
      </span>

      <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
        <Icon size={22} />
      </div>
      <div className="mt-5 text-xs uppercase tracking-[0.18em] text-brand-700 font-semibold">
        {t.eyebrow}
      </div>
      <h3 className="mt-2 text-2xl font-semibold tracking-tight text-ink-900">
        {t.title}
      </h3>
      <p className="mt-3 text-ink-500 text-sm leading-relaxed">{t.text}</p>
      {hasCta && (
        <span className="mt-auto pt-6 inline-flex items-center gap-2 text-sm font-medium text-ink-900 group-hover:gap-3 transition-all">
          {t.cta} <ArrowRight size={16} />
        </span>
      )}
    </>
  );
  const isExternal = t.external || /^https?:\/\//i.test(t.href || '');
  return (
    <div className="relative h-full" style={{ transitionDelay: `${i * 80}ms` }}>
      {isClickable ? (
        isExternal ? (
          <a
            href={t.href}
            ref={r.ref}
            className={`${r.className} ${cardBase} ${cardClickable}`}
          >
            {inner}
          </a>
        ) : (
          <Link
            to={t.href}
            ref={r.ref}
            className={`${r.className} ${cardBase} ${cardClickable}`}
          >
            {inner}
          </Link>
        )
      ) : (
        <div ref={r.ref} className={`${r.className} ${cardBase}`}>
          {inner}
        </div>
      )}
    </div>
  );
}

const ASSURANCES = [
  {
    title: 'Doctor-guided',
    body: 'Suitability is confirmed during doctor consultation',
  },
  {
    title: 'Verified Clinic Network',
    body: 'Access licensed clinics and qualified doctors, so you can proceed with greater confidence.',
  },
  {
    title: 'Private dashboard',
    body: 'Keep your updates organised in one place',
  },
];

function AssuranceBar() {
  const r = useReveal({ threshold: 0.1, base: 'reveal-scale' });
  return (
    <div
      ref={r.ref}
      className={`${r.className} mt-10 mx-auto max-w-4xl rounded-full bg-white border border-ink-100 shadow-card
                  px-4 sm:px-6 py-3 sm:py-3.5`}
    >
      <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-2">
        {ASSURANCES.map((a, i) => (
          <li key={a.title} className="flex items-center gap-2.5 px-2">
            <CheckCircle2
              size={20}
              className="text-brand-600 shrink-0 icon-breathe"
              strokeWidth={2.25}
              style={{ animationDelay: `${i * 0.4}s` }}
            />
            <div className="min-w-0">
              <div className="text-sm font-bold text-ink-900 leading-tight">{a.title}</div>
              <div className="text-[11px] sm:text-xs text-ink-500 leading-snug">{a.body}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function HomeTeaser() {
  const head = useReveal();
  return (
    <section className="relative isolate overflow-hidden bg-white pt-10 pb-16 sm:pt-12 sm:pb-20 lg:pt-14 lg:pb-24">
      {/* Background image — Background - Green Ai.png */}
      <img
        src="/Background - Green Ai.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 -z-20 w-full h-full object-cover"
      />
      {/* Soft white wash so text + cards stay legible over the background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-white/75"
      />

      <div className="container-x">
        <div ref={head.ref} className={`${head.className} flex flex-col md:flex-row md:items-end md:justify-between gap-6`}>
          <div className="max-w-2xl">
            <span className="eyebrow">How it works</span>
            <h2 className="h-section mt-4">Three steps. One guided journey.</h2>
            <p className="lead mt-4">
          Assessment, clinic matching, booking and follow-up — all in one guided experience with doctor consultation at every step.

            </p>
          </div>
          <div className="shrink-0 flex flex-col items-start md:items-end gap-2 self-start md:self-end">
            <Link
              to="/intake"
              className="cta-sheen cta-pulse-glow group inline-flex items-center gap-2 font-bold uppercase tracking-wide rounded-full
                         px-6 py-3.5 text-sm sm:text-base bg-brand-600 text-white hover:bg-brand-700
                         transition-colors shadow-card"
            >
              AM I QUALIFIED?
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/20
                               transition-transform duration-300 group-hover:translate-x-0.5">
                <ArrowRight size={14} />
              </span>
            </Link>
            <p className="text-[11px] sm:text-xs text-ink-500 max-w-xs md:text-right leading-snug">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-brand-700">Terms of Use</Link>
              {' '}and acknowledge our{' '}
              <Link to="/privacy" className="underline hover:text-brand-700">Privacy Policy</Link>.
            </p>
          </div>
        </div>

        <div className="relative mt-12">
          {/* Dotted connector line behind the cards (desktop only) */}
          <div
            aria-hidden
            className="hidden md:block absolute left-[16.66%] right-[16.66%] top-[3.5rem] h-px"
            style={{
              backgroundImage: 'repeating-linear-gradient(to right, #B6C3DF 0 6px, transparent 6px 14px)',
            }}
          />

          <div className="relative grid md:grid-cols-3 gap-6 items-stretch">
            {STEPS.map((t, i) => <StepCard key={t.title} t={t} i={i} />)}
          </div>

          <AssuranceBar />
        </div>
      </div>
    </section>
  );
}
